import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  legalLinkClass,
  legalListClass,
  LegalPageLayout,
  LegalSection,
} from "~/components/clawinstall/LegalPageLayout.tsx";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { type HeadCtxLike, identityFromHeadCtx, marketingHead } from "~/lib/clawinstall-marketing-head.ts";

/** Head meta tags for the Terms of Service page (brand name follows the request identity). */
export const termsHead = (ctx?: HeadCtxLike) => {
  const identity = identityFromHeadCtx(ctx);
  return marketingHead({
    path: "/terms",
    title: `Terms of Service — ${identity.displayName}`,
    description:
      `The terms governing your use of ${identity.displayName}'s AI Agent installation, hosting, and management services.`,
    identity,
  });
};

/**
 * ClawInstall Terms of Service page
 *
 * Renders the finalized Terms of Service (docs/legal/clawinstall-terms.md).
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/terms")({
  head: termsHead,
  component: ClawInstallTermsPage,
});

function ClawInstallTermsPage() {
  const identity = useAtomValue(clawInstallIdentityAtom);
  const name = identity.displayName;
  const nameUpper = name.toUpperCase();
  const legalEmail = <a href={`mailto:${identity.emails.legal}`} className={legalLinkClass}>{identity.emails.legal}</a>;
  return (
    <LegalPageLayout
      title={`${name} Terms of Service`}
      lastUpdated="September 5, 2026"
      version="1.1"
      current="/clawinstall/terms"
      testId="terms-page"
    >
      <section className="space-y-4">
        <p>
          <strong>Effective date:</strong> June 5, 2026
        </p>
        <p>
          These Terms of Service (“<strong>Terms</strong>”) form a binding agreement between you (“
          <strong>Customer</strong>”, “<strong>you</strong>”, or “<strong>your</strong>”) and{" "}
          <strong>{identity.legalName}</strong>, a California limited liability company{identity.dbaName === null
            ? null
            : (
              <>
                {" "}doing business as <strong>{identity.dbaName}</strong>
              </>
            )} (“<strong>{name}</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, or “<strong>our</strong>
          ”). They govern your access to and use of the {name} website at{" "}
          <a href={`https://${identity.canonicalHost}`} className={legalLinkClass}>{identity.canonicalHost}</a>, the AI
          Agent installation and hosting services described below, and any related software, dashboards, and support
          (collectively, the “<strong>Services</strong>”).
        </p>
        <p>
          By purchasing, accessing, or using the Services — including by clicking “I agree,” signing an order or quote,
          or allowing us to begin an installation — you agree to these Terms. If you are entering into these Terms on
          behalf of a company or other organization, you represent that you have authority to bind that organization,
          and “you” refers to that organization. If you do not agree, do not use the Services.
        </p>
      </section>

      <LegalSection title="1. Definitions">
        <ul className={legalListClass}>
          <li>
            <strong>“Agent”</strong>{" "}
            means an AI agent (including instances of the open-source OpenClaw agent framework and any models, skills,
            or integrations we configure) that we install, host, or manage for you under these Terms.
          </li>
          <li>
            <strong>“Cloud Node”</strong> means a virtual private server or other cloud compute instance that {name}
            {" "}
            provisions and operates on your behalf to run one or more Agents.
          </li>
          <li>
            <strong>“Customer Mac”</strong>{" "}
            means a Mac computer (for example, a Mac mini or MacBook) or other hardware that you own or control, onto
            which we install and configure Agents.
          </li>
          <li>
            <strong>“Deployment”</strong> means a Cloud Node or Customer Mac on which Agents run.
          </li>
          <li>
            <strong>“Credentials”</strong>{" "}
            means any account credentials, OAuth authorizations, API keys, access tokens, or other authentication
            material you provide or authorize so that Agents can act on your behalf with Third-Party Platforms.
          </li>
          <li>
            <strong>“Third-Party Platform”</strong>{" "}
            means any third-party product, network, model provider, or service that an Agent connects to or relies on,
            including messaging platforms (e.g., Discord, Telegram, WhatsApp), AI model providers, and infrastructure
            providers (e.g., Tailscale, Let's Encrypt, hosting and payment providers).
          </li>
          <li>
            <strong>“Reseller”</strong>{" "}
            means an independent third party authorized to market, resell, or arrange the Services for end customers.
          </li>
          <li>
            <strong>“Order”</strong>{" "}
            means a quote, checkout, order form, or other ordering document referencing these Terms.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="2. The Services">
        <p>
          {name}{" "}
          provides professional installation, configuration, security hardening, hosting, and ongoing management of AI
          Agents. Depending on your Order, the Services may include:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>Installation and setup</strong>{" "}
            of one or more Agents on a Cloud Node we provision, or on a Customer Mac you provide;
          </li>
          <li>
            <strong>Security hardening</strong>{" "}
            of the Deployment (for example, VPN-tunneled remote access, SSH lockdown, firewall configuration, TLS
            certificates, automatic OS security updates, and non-root process isolation);
          </li>
          <li>
            <strong>Hosting and management</strong>, including monitoring, maintenance, updates, and support, where you
            purchase a recurring management or subscription plan; and
          </li>
          <li>
            <strong>Configuration of integrations</strong>{" "}
            that allow Agents to act on your behalf with Third-Party Platforms you authorize.
          </li>
        </ul>
        <p>
          We may modify, improve, or discontinue features of the Services from time to time. We will use commercially
          reasonable efforts to notify you of material changes that adversely affect an active paid Deployment.
        </p>
      </LegalSection>

      <LegalSection title="3. Deployment Options and Responsibilities">
        <p>
          <strong>3.1 Cloud Node.</strong>{" "}
          If your Order includes a Cloud Node, we provision and operate the underlying server through our hosting and
          infrastructure providers. You are responsible for the lawful use of the Agents running on it. We do not
          guarantee uninterrupted availability of any Cloud Node (see Section 11).
        </p>
        <p>
          <strong>3.2 Customer Mac.</strong>{" "}
          If you elect a Customer Mac deployment, you are responsible for providing eligible hardware, a stable network
          connection, electrical power, physical security, and any prerequisite software (for example, a VPN client) we
          reasonably require. We are not responsible for hardware failure, loss, theft, network outages, OS-level
          changes you make, or other conditions of the Customer Mac outside our control.
        </p>
        <p>
          <strong>3.3 Access.</strong>{" "}
          You grant us the access necessary to perform the Services, including remote access to the Deployment for
          installation, maintenance, and support. We will use such access only as needed to deliver the Services.
        </p>
      </LegalSection>

      <LegalSection title="4. Customer Responsibilities and Credential Authorization">
        <p>
          <strong>4.1 Authorization to act on your behalf.</strong>{" "}
          You acknowledge and agree that Agents are designed to act <strong>on your behalf</strong>{" "}
          using Credentials you provide or authorize (including via OAuth). By providing or authorizing Credentials,
          you:
        </p>
        <ul className={legalListClass}>
          <li>
            represent that you are the owner of, or are otherwise authorized to use and delegate, each connected account
            and that doing so does not violate any agreement with, or policy of, the relevant Third-Party Platform;
          </li>
          <li>
            authorize {name}{" "}
            and the Agents to access and use those accounts to perform the actions you configure or request, within the
            scope you grant; and
          </li>
          <li>
            understand that Agents may send messages, post content, read data, and take other actions in the connected
            accounts as if taken by you.
          </li>
        </ul>
        <p>
          <strong>4.2 Scope and revocation.</strong>{" "}
          The authorization in Section 4.1 is limited to the scope of access you grant and remains in effect until you
          revoke it (for example, by removing the OAuth authorization or terminating the Services). Revoking Credentials
          may disable or degrade affected Agents.
        </p>
        <p>
          <strong>4.3 Your conduct.</strong>{" "}
          You are responsible for all activity conducted through your Deployment and Agents. You will use the Services
          in compliance with these Terms, our{" "}
          <Link to="/clawinstall/acceptable-use" className={legalLinkClass}>Acceptable Use Policy</Link>, all applicable
          laws, and the terms and policies of every Third-Party Platform you connect. You will not use the Services to
          send spam or unlawful communications, to harass or harm others, to infringe intellectual-property or privacy
          rights, or for any prohibited use described in the Acceptable Use Policy.
        </p>
        <p>
          <strong>4.4 Account security.</strong>{" "}
          You are responsible for safeguarding your own accounts and Credentials and for promptly notifying us of any
          suspected unauthorized use.
        </p>
      </LegalSection>

      <LegalSection title="5. Fees, Subscriptions, Auto-Renewal, and Taxes">
        <p>
          <strong>5.1 Fees.</strong> The Services are offered as a combination of (a){" "}
          <strong>one-time installation/setup fees</strong> and (b){" "}
          <strong>recurring per-Agent subscription fees</strong>{" "}
          for hosting and management, each as set out in your Order or on our pricing page. Fees are stated in U.S.
          dollars unless otherwise specified.
        </p>
        <p>
          <strong>5.2 Billing and payment processor.</strong>{" "}
          Payments are processed by our third-party payment processor,{" "}
          <strong>Stripe</strong>. By providing a payment method, you authorize us and Stripe to charge all fees due,
          including recurring subscription fees, applicable taxes, and any overage or add-on fees you order. Your use of
          Stripe is also subject to Stripe's terms and privacy policy.
        </p>
        <p>
          <strong>5.3 Auto-renewal.</strong>{" "}
          Subscription plans renew automatically at the end of each billing period (monthly unless your Order states
          otherwise) at the then-current rate,{" "}
          <strong>until you cancel</strong>. By subscribing, you authorize these recurring charges. You may cancel
          renewal at any time as described in Section 5.5; cancellation stops future renewals but does not retroactively
          cancel the current period.
        </p>
        <p>
          <strong>5.4 Price changes.</strong>{" "}
          We may change fees for future billing periods. We will provide at least thirty (30) days' notice of a price
          increase to an active subscription, and the new price applies on your next renewal after the notice period.
          Continuing to use the Services after the change takes effect constitutes acceptance of the new price.
        </p>
        <p>
          <strong>5.5 Cancellation.</strong>{" "}
          You may cancel a subscription at any time through your account dashboard or by contacting us at{" "}
          <a href={`mailto:${identity.emails.billing}`} className={legalLinkClass}>{identity.emails.billing}</a>.{" "}
          Cancellation takes effect at the end of the then-current billing period; you retain access to the subscribed
          Services through that period unless we terminate earlier under Section 12.
        </p>
        <p>
          <strong>5.6 Refund policy.</strong> Except where required by law:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>One-time installation/setup fees are non-refundable once installation work has begun.</strong>{" "}
            If we are unable to complete a paid installation due to a cause attributable solely to us, your sole remedy
            is a refund of the installation fee for that incomplete installation.
          </li>
          <li>
            <strong>Recurring subscription fees are non-refundable</strong>{" "}
            for the billing period in which they are charged. When you cancel, you will not be charged for subsequent
            periods, but we do not provide pro-rated refunds for partial periods.
          </li>
        </ul>
        <p>
          <strong>5.7 Late and failed payments.</strong>{" "}
          If a charge fails or a payment is past due, we may retry the charge, suspend the affected Services, and/or
          terminate the subscription as described in Section 12. You remain responsible for amounts owed.
        </p>
        <p>
          <strong>5.8 Taxes.</strong>{" "}
          Fees are exclusive of taxes. You are responsible for all sales, use, value-added, and similar taxes (excluding
          taxes on our net income). If we are required to collect such taxes, they will be added to your invoice.
        </p>
      </LegalSection>

      <LegalSection title="6. Third-Party Platforms">
        <p>
          The Services interoperate with Third-Party Platforms that we do not own or control. Your use of any
          Third-Party Platform is governed by that provider's terms and policies, and{" "}
          <strong>
            {name} is not responsible for, and makes no warranties regarding, any Third-Party Platform
          </strong>, including its availability, security, pricing, rate limits, policy enforcement (such as account
          suspensions or bans), or changes to its APIs. A Third-Party Platform may suspend, limit, or terminate access
          to its services, including access by Agents acting on your behalf, and {name}{" "}
          is not liable for any resulting interruption or loss. You are responsible for maintaining your own accounts
          and entitlements with each Third-Party Platform.
        </p>
      </LegalSection>

      <LegalSection title="7. AI Output Disclaimer">
        <p>
          Agents use artificial-intelligence models that generate outputs probabilistically. AI-generated outputs{" "}
          <strong>may be inaccurate, incomplete, offensive, or otherwise unsuitable</strong>, and may not reflect
          {name}'s views. You are responsible for reviewing and validating Agent outputs before relying on or acting on
          them, and for any actions an Agent takes on your behalf under the authorization you grant. The Services are
          {" "}
          <strong>not</strong>{" "}
          a substitute for professional advice (including legal, financial, medical, or other professional advice). You
          should not configure Agents to take consequential actions without appropriate human review.
        </p>
      </LegalSection>

      <LegalSection title="8. Intellectual Property and Open-Source Software">
        <p>
          <strong>8.1 {name} IP.</strong> As between the parties, {name}{" "}
          and its licensors own all rights in the Services, including our website, dashboards, configurations,
          documentation, and trademarks (including “ClawInstall” and “Consinna”). We grant you a limited, non-exclusive,
          non-transferable, revocable right to use the Services during the term, subject to these Terms. No rights are
          granted except as expressly stated.
        </p>
        <p>
          <strong>8.2 Open-source software.</strong>{" "}
          The Services install and rely on open-source software, including the <strong>OpenClaw</strong>{" "}
          agent framework, which is licensed to you under its own open-source license, not under these Terms. Your use
          of such open-source components is governed by the applicable open-source licenses, and nothing in these Terms
          limits your rights, or adds obligations beyond what those licenses require, with respect to that software. We
          do not own, and make no proprietary claim to, third-party open-source software.
        </p>
        <p>
          <strong>8.3 Your content.</strong>{" "}
          You retain ownership of the data, content, and Credentials you provide. You grant us a limited license to
          host, process, and use them solely to provide, maintain, secure, and support the Services.
        </p>
        <p>
          <strong>8.4 Feedback.</strong>{" "}
          If you give us feedback or suggestions, you grant us a perpetual, irrevocable, royalty-free license to use it
          without restriction.
        </p>
      </LegalSection>

      <LegalSection title="9. Data Protection and Privacy">
        <p>
          Our collection and use of personal information in connection with the Services is described in our{" "}
          <Link to="/clawinstall/privacy" className={legalLinkClass}>Privacy Policy</Link>, which is incorporated into
          these Terms by reference. To the extent we process personal data on your behalf as a processor, that
          processing is governed by a separate data processing agreement (“<strong>DPA</strong>”); where required by
          applicable law and requested by you, the parties will enter into{" "}
          {name}'s then-current DPA, which will control over these Terms with respect to such processing. You are
          responsible for providing any notices to, and obtaining any consents from, individuals whose data is made
          available to the Services or to Agents acting on your behalf.
        </p>
      </LegalSection>

      <LegalSection title="10. Resellers">
        <p>
          The Services may be marketed or arranged by an independent <strong>Reseller</strong>.{" "}
          <strong>
            Each Reseller is an independent third party and is not an agent, partner, employee, or representative of
            {" "}
            {name}
          </strong>, and {name}{" "}
          is not responsible for the acts, omissions, representations, or agreements of any Reseller. Pricing, billing,
          and support arrangements that a Reseller offers may differ from ours; any commitment a Reseller makes that is
          not contained in these Terms or in an Order issued or accepted by {name} does not bind{" "}
          {name}. If you purchased through a Reseller, these Terms still govern your use of the Services, and certain
          billing or support matters may be handled by that Reseller as disclosed at purchase.
        </p>
      </LegalSection>

      <LegalSection title="11. Warranty Disclaimer">
        <p>
          THE SERVICES, INCLUDING ALL AGENTS, DEPLOYMENTS, SOFTWARE, AND AI OUTPUTS, ARE PROVIDED{" "}
          <strong>“AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND</strong>, WHETHER EXPRESS, IMPLIED, OR
          STATUTORY. TO THE MAXIMUM EXTENT PERMITTED BY LAW, {nameUpper}{" "}
          DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
          NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE FROM
          HARMFUL COMPONENTS, OR THAT AGENT OUTPUTS WILL BE ACCURATE OR RELIABLE. NO ADVICE OR INFORMATION OBTAINED FROM
          US CREATES ANY WARRANTY NOT EXPRESSLY STATED HERE. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN
          WARRANTIES, SO SOME OF THE ABOVE MAY NOT APPLY TO YOU.
        </p>
      </LegalSection>

      <LegalSection title="12. Term, Suspension, and Termination">
        <p>
          <strong>12.1 Term.</strong>{" "}
          These Terms apply from your first use of the Services and continue until all subscriptions are terminated and
          no Services remain active.
        </p>
        <p>
          <strong>12.2 Termination for convenience.</strong>{" "}
          You may stop using the Services and cancel subscriptions at any time under Section 5.5. We may terminate or
          decline to renew a subscription on at least thirty (30) days' notice.
        </p>
        <p>
          <strong>12.3 Suspension.</strong>{" "}
          We may suspend the Services, in whole or in part, immediately if (a) your payment is past due, (b) we
          reasonably believe your use poses a security, legal, or operational risk, or violates the Acceptable Use
          Policy, or (c) a Third-Party Platform or law requires it. We will use reasonable efforts to give notice where
          practicable.
        </p>
        <p>
          <strong>12.4 Termination for cause and cure period.</strong>{" "}
          Either party may terminate these Terms for the other party's material breach if the breaching party fails to
          cure the breach within <strong>thirty (30) days</strong>{" "}
          after receiving written notice describing it. We may terminate immediately for breaches that cannot reasonably
          be cured, including violations of Section 4 (Customer Responsibilities) or the Acceptable Use Policy.
        </p>
        <p>
          <strong>12.5 Effect of termination.</strong>{" "}
          On termination, your right to use the Services ends and we may deactivate Agents and deprovision Cloud Nodes.
          For a Customer Mac deployment, we may remove or disable the Agent software we installed. You remain
          responsible for fees accrued before termination. Sections that by their nature should survive (including
          Sections 5, 7, 8, 11, 13, 14, and 15) survive termination.
        </p>
      </LegalSection>

      <LegalSection title="13. Limitation of Liability">
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
        <p>
          <strong>13.1 Exclusion of indirect damages.</strong>{" "}
          NEITHER PARTY WILL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
          DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO
          THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          <strong>13.2 Cap.</strong>{" "}
          {nameUpper}'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES WILL NOT
          EXCEED THE TOTAL FEES YOU ACTUALLY PAID TO {nameUpper} FOR THE SERVICES IN THE{" "}
          <strong>TWELVE (12) MONTHS</strong> IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE LIABILITY.
        </p>
        <p>
          <strong>13.3 Basis of the bargain.</strong>{" "}
          These limitations apply regardless of the theory of liability and even if a remedy fails of its essential
          purpose. Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.
        </p>
      </LegalSection>

      <LegalSection title="14. Indemnification">
        <p>
          You will defend, indemnify, and hold harmless {name}{" "}
          and its affiliates, officers, directors, employees, and agents from and against any third-party claims,
          damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to
          (a) your use of the Services, (b) your data, content, or Credentials, or any action an Agent takes on your
          behalf under the authorization you grant, (c) your violation of these Terms, the Acceptable Use Policy,
          applicable law, or any Third-Party Platform's terms, or (d) your infringement or misappropriation of any third
          party's rights. We will notify you of the claim, give you control of the defense (subject to our right to
          participate with our own counsel), and reasonably cooperate at your expense. You may not settle a claim in a
          way that imposes obligations on us without our prior written consent.
        </p>
      </LegalSection>

      <LegalSection title="15. Dispute Resolution; Arbitration; Class-Action Waiver">
        <p>
          <strong>PLEASE READ THIS SECTION CAREFULLY — IT AFFECTS HOW DISPUTES ARE RESOLVED.</strong>
        </p>
        <p>
          <strong>15.1 Informal resolution.</strong>{" "}
          Before filing a claim, the parties agree to try to resolve the dispute informally by sending written notice to
          {" "}
          {legalEmail} and negotiating in good faith for at least thirty (30) days.
        </p>
        <p>
          <strong>15.2 Binding arbitration.</strong>{" "}
          Except for the carve-outs in Section 15.4, any dispute, claim, or controversy arising out of or relating to
          these Terms or the Services will be resolved by <strong>final and binding arbitration</strong>{" "}
          administered by the American Arbitration Association (“<strong>AAA</strong>”) under its Commercial Arbitration
          Rules (and Consumer Arbitration Rules where applicable). The arbitration will be conducted in the State of
          Delaware, or by videoconference, and judgment on the award may be entered in any court of competent
          jurisdiction.
        </p>
        <p>
          <strong>15.3 Class-action waiver.</strong>{" "}
          <strong>
            All claims must be brought in the parties' individual capacity, and not as a plaintiff or class member in
            any purported class, collective, consolidated, or representative proceeding.
          </strong>{" "}
          The arbitrator may not consolidate more than one person's claims and may not preside over any class or
          representative proceeding. If this class-action waiver is found unenforceable as to a particular claim, that
          claim (and only that claim) will be severed and brought in court, while the rest remains in arbitration.
        </p>
        <p>
          <strong>15.4 Exceptions.</strong>{" "}
          Either party may (a) bring an individual claim in small-claims court, and (b) seek injunctive or equitable
          relief in a court of competent jurisdiction to protect its intellectual property or Confidential Information.
          Nothing here waives any right that cannot be waived under applicable law.
        </p>
        <p>
          <strong>15.5 Opt-out.</strong> You may opt out of this Section 15 by emailing {legalEmail}{" "}
          within thirty (30) days of first accepting these Terms, stating your name and intent to opt out. Opting out
          does not affect any other part of these Terms.
        </p>
      </LegalSection>

      <LegalSection title="16. Governing Law and Venue">
        <p>
          These Terms are governed by the laws of the{" "}
          <strong>State of Delaware</strong>, without regard to its conflict-of-laws rules, and excluding the U.N.
          Convention on Contracts for the International Sale of Goods. Subject to Section 15, any dispute not resolved
          by arbitration will be brought exclusively in the state or federal courts located in the State of Delaware,
          and the parties consent to personal jurisdiction and venue there.
        </p>
      </LegalSection>

      <LegalSection title="17. General">
        <p>
          <strong>17.1 Changes to these Terms.</strong>{" "}
          We may update these Terms from time to time. When we make material changes, we will update the “Last updated”
          date and version above and provide reasonable notice (for example, by posting on the website or notifying
          you). Changes take effect when posted unless stated otherwise; your continued use after the effective date
          constitutes acceptance. The version string in effect when you accept is the version that governs your
          acceptance.
        </p>
        <p>
          <strong>17.2 Notices.</strong> Legal notices to {name} must be sent to{" "}
          {legalEmail}. We may send notices to you at the email or account contact on file.
        </p>
        <p>
          <strong>17.3 Assignment.</strong>{" "}
          You may not assign these Terms without our prior written consent. We may assign these Terms to an affiliate or
          in connection with a merger, acquisition, or sale of assets. These Terms bind permitted successors and
          assigns.
        </p>
        <p>
          <strong>17.4 Entire agreement.</strong>{" "}
          These Terms, together with any Order, the Acceptable Use Policy, and the Privacy Policy, are the entire
          agreement between the parties regarding the Services and supersede all prior or contemporaneous agreements on
          that subject.
        </p>
        <p>
          <strong>17.5 Severability and waiver.</strong>{" "}
          If any provision is found unenforceable, it will be limited or severed to the minimum extent necessary, and
          the remaining provisions remain in effect. A party's failure to enforce a provision is not a waiver.
        </p>
        <p>
          <strong>17.6 Force majeure.</strong>{" "}
          Neither party is liable for delays or failures caused by events beyond its reasonable control, including acts
          of God, outages of Third-Party Platforms or infrastructure providers, network or power failures, or
          governmental action.
        </p>
        <p>
          <strong>17.7 Relationship.</strong>{" "}
          The parties are independent contractors. These Terms do not create any partnership, joint venture, agency, or
          employment relationship.
        </p>
        <p>
          <strong>17.8 Contact.</strong> Questions about these Terms may be sent to {legalEmail}.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
